import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Download, Loader2, XCircle, FolderDown } from "lucide-react";
import { Header } from "@/components/Header";
import { api, thumbUrl, downloadUrl, downloadAllUrl, brl } from "@/lib/api";
import { useCart } from "@/lib/cart";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const session = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!session) return setStatus("error");
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${session}`);
        if (data.payment_status === "paid") {
          setOrder((await api.get(`/orders/${session}`)).data);
          setStatus("paid");
          clear();
          return;
        }
        if (["expired", "failed", "refunded"].includes(data.payment_status)) return setStatus("error");
      } catch {
        return setStatus("error");
      }
      if (++tries < 120) timer = setTimeout(poll, tries < 15 ? 2000 : 4000);
      else setStatus("timeout");
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
        {status === "checking" && (
          <div className="flex items-center gap-3 text-zinc-600" data-testid="payment-checking"><Loader2 className="w-5 h-5 animate-spin" />
            <div>
              <div className="font-semibold text-zinc-900">Confirmando pagamento…</div>
              <div className="text-sm">Pagou com Pix? A confirmação costuma levar alguns segundos. Não feche esta página.</div>
            </div>
          </div>
        )}
        {(status === "error" || status === "timeout") && (
          <div data-testid="payment-error">
            <XCircle className="w-10 h-10 text-red-500" />
            <h1 className="mt-4 font-display font-bold text-3xl">{status === "timeout" ? "Ainda processando" : "Pagamento não confirmado"}</h1>
            <p className="mt-2 text-zinc-600">{status === "timeout" ? "Atualize esta página em alguns instantes." : "Não conseguimos confirmar este pagamento."}</p>
            {status === "timeout" ? (
              <button onClick={() => window.location.reload()} data-testid="payment-retry-btn" className="mt-6 h-12 px-6 rounded-md bg-[#0055FF] text-white font-semibold">Verificar novamente</button>
            ) : (
              <Link to="/carrinho" className="mt-6 inline-block text-[#0055FF] underline">Voltar ao carrinho</Link>
            )}
          </div>
        )}
        {status === "paid" && order && (
          <div data-testid="payment-success">
            <CheckCircle2 className="w-10 h-10 text-[#34C759]" />
            <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl tracking-tighter">Pagamento aprovado!</h1>
            <p className="mt-3 text-zinc-600">
              {order.photos.length} foto(s) · {brl(order.amount)}. Baixe agora — também enviamos o link para {order.email}.
            </p>
            <a
              href={downloadAllUrl(session)}
              data-testid="download-all-btn"
              className="mt-6 inline-flex items-center gap-2 h-14 px-7 rounded-md bg-[#0055FF] hover:bg-[#0040CC] text-white text-lg font-semibold transition-colors"
            >
              <FolderDown className="w-6 h-6" /> Baixar todas as fotos agora
            </a>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="download-grid">
              {order.photos.map((p) => (
                <div key={p.id} className="border border-zinc-200 rounded-md overflow-hidden">
                  <img src={thumbUrl(p.id)} alt="" className="w-full aspect-[4/5] object-cover" />
                  <a
                    href={downloadUrl(session, p.id)}
                    data-testid={`download-btn-${p.id}`}
                    className="flex items-center justify-center gap-2 h-11 bg-zinc-900 hover:bg-[#0055FF] text-white text-sm font-semibold transition-colors"
                  >
                    <Download className="w-4 h-4" /> Baixar original
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
